import {computed, makeObservable, observable} from 'mobx';

import type {AccountType, Role as ApiRole} from '../../../api/user';
import type {Nullable} from '../../../utils/utility-types';
import type {CategoryModel} from '../Category/Category.model';
import type {PrivateProfileModel} from '../common/PrivateProfile/PrivateProfile.model';
import type {UserAdditionalDataModel} from '../common/UserAdditionalData/UserAdditionalData.model';
import type {FileModel} from '../File/File.model';
import type {SubtenantModel} from '../Subtenant/Subtenant.model';
import type {UserMetadataModel} from './UserMetadata/UserMetadata.model';
import type {UserOnboardingModel} from './UserOnboarding/UserOnboarding.model';

import {BaseModel} from '../Base/Base.model';
import {DateTimeModel} from '../Date/DateTime.model';

const ModelName = 'UserModel';

export class UserModel extends BaseModel {
  accountType: Nullable<UserModel.UserAccountType>;
  additionalData: Nullable<UserAdditionalDataModel>;
  avatar: Nullable<FileModel>;
  coachLogo: Nullable<FileModel>;
  coachMaxClients: number;
  coachSubtenants: SubtenantModel[];
  coachTitle: string;
  createdAt: DateTimeModel;
  description: string;
  externalId: Nullable<string>;
  gender: Nullable<UserModel.Gender>;
  isCoach: boolean;
  isCorporateCoach: boolean;
  isDeleted: boolean;
  isSupport: boolean;
  lastSeenAt: Nullable<DateTimeModel>;
  location: Nullable<string>;
  metadata: UserMetadataModel;
  name: string;
  onboarding: UserOnboardingModel;
  populations: CategoryModel[];
  privateProfile: PrivateProfileModel;
  roles: UserModel.Role[];
  slug: string;
  source: UserModel.UserSource;
  subtenant: Nullable<SubtenantModel>;
  timezone: Nullable<string>;
  visible_widgets: UserModel.VisibleWidgetsKey[];
  yearOfBirth: Nullable<number>;

  constructor(props: UserModel.Props) {
    super(props);
    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<UserModel, UserModel.Props>(props, {
        isDtc: computed,
        populations: observable.shallow,
      }),
    );
  }

  get hasArticlesAccess() {
    if (this.isCoach) {
      return this.coachSubtenants.some(
        subtenant => subtenant.articlesAccess.hasAccess,
      );
    }

    return this.subtenant?.articlesAccess.hasAccess ?? false;
  }

  get isDtc() {
    return Boolean(this.metadata?.is_dtc);
  }

  get isEmployee() {
    return this.metadata.onboarding?.account_type === 'employee';
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  override update(props: UserModel.UpdateProps) {
    if (props.privateProfile?.dateOfBirth instanceof DateTimeModel) {
      const yearString = props.privateProfile.dateOfBirth.format('YYYY');

      props.yearOfBirth = parseInt(yearString, 10);
    }

    return super.update(props as BaseModel.Source<this>);
  }
}

export namespace UserModel {
  export type Gender = 'female' | 'male' | 'na' | 'other';

  export type Onboarding = {
    answers: {
      answer: string[];
      question: string;
    }[];
    finished: Nullable<DateTimeModel>;
    type: Nullable<'b2b_widget' | 'external' | 'internal' | 'tenant_manager'>;
  };

  export type Props = BaseModel.ModelToProps<
    UserModel,
    'hasArticlesAccess' | 'isDtc' | 'isEmployee'
  >;

  export type Role = ApiRole;

  export type UpdateProps = BaseModel.Source<UserModel>;

  export type UserAccountType = AccountType;

  export type UserSource = 'b2b_widget' | 'direct';

  export type VisibleWidgetsKey = 'current_weight' | 'weight_history';
}
